'use client'

import type { FC } from 'react'
import { AddressItemCardIcon } from '../address-list-icon/AddressItemCardIcon'
import { checkHasMissingAddressDetails } from '../helpers'
import type { AddressListItem } from '../types'
import styles from './AddressItemCard.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import {
  EditIcon,
  ExclamationTriangleIcon,
  IconBlob,
  IconBlobType,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
  IconSize,
  ListItem,
  LocationArrowIcon,
  Tag,
  TagStyle,
  TagVariant,
} from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { addressLandingOptionClickedEvent } from '@/user/address-book/analytics'
import type { UserAddressTag } from '@/user/address-book/entities'
import { ADDRESS_TAG_T_KEYS } from '@/user/address-book/ui/constants'

interface Props {
  addressItem: AddressListItem
  isLastItem: boolean
}

export const AddressItemCard: FC<Props> = ({ addressItem, isLastItem }) => {
  const { withErrorTracking } = useErrorHandling()
  const { t } = useLocalisation()
  const { setAddress, navigate, selectAddress, setAddressFormTrigger, setOriginalCoordinates } =
    useAddressBookContext()

  const addressDetails = addressItem.isCurrentLocation
    ? t('address-list.current-location')
    : addressItem.details || ''
  const hasMissingDetails = checkHasMissingAddressDetails(addressItem)
  const shouldShowDetailsText = !hasMissingDetails || addressItem.isCurrentLocation

  const getFormattedAddressLine = () => {
    const tag = ADDRESS_TAG_T_KEYS[addressItem.tag as UserAddressTag]
    const effectiveTag = tag ? t(tag) : addressItem.tag
    return addressItem.tag
      ? `${effectiveTag} - ${addressItem.addressLine}`
      : addressItem.addressLine
  }

  const handleSelectAddress = withErrorTracking(async () => {
    if (hasMissingDetails) {
      setAddress(addressItem)
      setOriginalCoordinates(addressItem)
      navigate('onEditAddressWithoutDetails')
    } else {
      selectAddress(addressItem)
    }
    addressLandingOptionClickedEvent('SavedAddress')
  })

  const handleEditAddressClick = withErrorTracking((ev: React.MouseEvent) => {
    ev.stopPropagation()
    setAddress(addressItem)
    setOriginalCoordinates(addressItem)
    setAddressFormTrigger('SavedAddressEdit')
    hasMissingDetails ? navigate('onEditAddressWithoutDetails') : navigate('onEditAddress')
  })

  const trailingContent = (
    <IconButton
      aria-label={t('address-list.edit-address-button')}
      icon={EditIcon}
      size={IconButtonSize.small}
      variant={IconButtonVariants.neutral}
      onClick={handleEditAddressClick}
    />
  )

  return (
    <ListItem
      actionable={true}
      className={styles.addressItemWrapper}
      trailingContent={trailingContent}
      withDivider={isLastItem ? false : true}
      onClick={handleSelectAddress}
    >
      <div className={styles.addressItemCard}>
        {addressItem.isCurrentLocation ? (
          <IconBlob icon={LocationArrowIcon} size={IconSize.medium} type={IconBlobType.positive} />
        ) : (
          <AddressItemCardIcon addressKind={addressItem.kind} />
        )}
        <div className={styles.address}>
          <p>{getFormattedAddressLine()}</p>

          {shouldShowDetailsText && <p className={styles.details}>{addressDetails}</p>}

          {hasMissingDetails && (
            <Tag
              className={styles.tag}
              label={t('address-list.missing-details-tag')}
              leadingIcon={ExclamationTriangleIcon}
              style={TagStyle.warning}
              variant={TagVariant.secondary}
            />
          )}
        </div>
      </div>
    </ListItem>
  )
}
